import {
  AppstoreOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  FormOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import McpAccessPage from '@/features/mcp/access/page'
import FirmProfilePage from '@/features/mcp/firmProfile/page'
import PlaybooksPage from '@/features/mcp/playbooks/page'
import FileUpload from '../../pages/FileUpload'
import ClientProjectStatusPage from '../../pages/clientTabs/ClientProjectStatusPage'
import ReportViewPage from '../../pages/ReportViewPage'
import ClientReportPage from '../../pages/clientTabs/ClientReportPage'
import ClientStaffPage from '../../pages/clientTabs/ClientStaffPage'
import ClientSurveyFormPage from '../../pages/clientTabs/ClientSurveyFormPage'
import ManageNotificationOptionsPage from '../../pages/reports/ManageNotificationOptionsPage'
import NotificationOptionSchedulesPage from '../../pages/reports/NotificationOptionSchedulesPage'
import ReportBuilderPage from '../../pages/reports/ReportBuilderPage'
import ReportOptionsPage from '../../pages/reports/ReportOptionsPage'
import ReportSchedulesPage from '../../pages/reports/ReportSchedulesPage'
import ReportsCreatedPage from '../../pages/reports/ReportsCreatedPage'
import ReportsTemplatesPage from '../../pages/reports/ReportsTemplatesPage'
import AgentBuilderHomePage from '../../features/dashboard-builder/home'
import DashboardViewerPage from '../../features/dashboard-builder/viewer/page'
import { permissions } from '../../types/authorization'
import type { ProtectedRouteDescriptor } from '../types'

export const practiceRoutes: ProtectedRouteDescriptor[] = [
  {
    key: 'client-report',
    path: 'report',
    element: <ClientReportPage />,
    access: { anyOf: [permissions.viewReport, permissions.viewUserReports] },
    nav: {
      sectionKey: 'practice',
      label: 'Dashboards',
      Icon: AppstoreOutlined,
      order: 10,
    },
    visibility: { requiresClient: true },
  },
  {
    key: 'client-report-view',
    path: 'report/:reportId',
    element: <ReportViewPage source="practice" />,
    access: { anyOf: [permissions.viewReport, permissions.viewUserReports] },
  },
  {
    key: 'client-staff',
    path: 'staff',
    element: <ClientStaffPage />,
    access: { anyOf: [permissions.viewStaffData] },
    nav: {
      sectionKey: 'practice',
      label: 'Staff Data',
      Icon: TeamOutlined,
      order: 30,
    },
    visibility: { requiresClient: true },
  },
  {
    key: 'client-surveyforms',
    path: 'surveyforms',
    element: <ClientSurveyFormPage />,
    access: { anyOf: [permissions.viewSurveyForm] },
    nav: {
      sectionKey: 'practice',
      label: 'Survey Forms',
      Icon: FormOutlined,
      order: 35,
    },
    visibility: { requiresClient: true },
  },
  {
    key: 'client-project-status',
    path: 'project-status',
    element: <ClientProjectStatusPage />,
    access: { anyOf: [permissions.viewProjectStatus] },
    nav: {
      sectionKey: 'practice',
      label: 'Project Status',
      Icon: CheckSquareOutlined,
      order: 32,
    },
    visibility: {
      requiresClient: true,
      clientFlags: { showProjectStatus: true },
    },
  },
  {
    key: 'reports-root',
    path: 'reports',
    element: <ReportsCreatedPage />,
    redirectTo: '/reports/created',
  },
  {
    key: 'reports-created',
    path: 'reports/created',
    element: <ReportsCreatedPage />,
    access: { anyOf: [permissions.viewNotification] },
    nav: {
      sectionKey: 'practice',
      label: 'PracIntel',
      Icon: FileTextOutlined,
      order: 20,
      // Agent Builder owns the PracIntel slot when the client has that feature,
      // so this stands in for it otherwise. Nav-only: the route stays reachable
      // either way, since Agent Builder links back here and this is a preferred
      // landing path for users with no active client.
      access: { noneOf: [permissions.viewAgentBuilder] },
    },
  },
  {
    key: 'reports-templates',
    path: 'reports/templates',
    element: <ReportsTemplatesPage />,
    access: { anyOf: [permissions.viewNotification] },
  },
  {
    key: 'reports-playbooks',
    path: 'reports/playbooks',
    element: <PlaybooksPage />,
    access: { anyOf: [permissions.viewAgentBuilder] },
    visibility: { requiresClient: true },
  },
  {
    key: 'reports-firm-profile',
    path: 'reports/firm-profile',
    element: <FirmProfilePage />,
    access: { allOf: [permissions.viewAgentBuilder, permissions.manageClaudeAccess] },
    visibility: { requiresClient: true },
  },
  {
    key: 'reports-mcp-access',
    path: 'reports/mcp-access',
    element: <McpAccessPage />,
    access: { anyOf: [permissions.manageClaudeAccess] },
    visibility: { requiresClient: true },
  },
  {
    key: 'reports-dashboard-builder',
    path: 'reports/dashboard-builder',
    element: <AgentBuilderHomePage />,
    access: { anyOf: [permissions.viewAgentBuilder] },
    nav: {
      sectionKey: 'practice',
      label: 'PracIntel',
      Icon: FileTextOutlined,
      order: 20,
    },
    visibility: { requiresClient: true },
  },
  {
    key: 'reports-dashboard-builder-view',
    path: 'reports/dashboard-builder/view',
    element: <DashboardViewerPage />,
    access: { anyOf: [permissions.viewAgentBuilder] },
    visibility: { requiresClient: true },
  },
  {
    key: 'reports-custom-new',
    path: 'reports/custom/new',
    element: <ReportBuilderPage />,
    access: { anyOf: [permissions.viewNotification] },
  },
  {
    key: 'reports-custom-edit',
    path: 'reports/custom/:reportBuilderId',
    element: <ReportBuilderPage />,
    access: { anyOf: [permissions.viewNotification] },
  },
  {
    key: 'reports-custom-schedule-edit',
    path: 'reports/custom/:reportBuilderId/:scheduleId',
    element: <ReportBuilderPage />,
    access: { anyOf: [permissions.viewNotification] },
  },
  {
    key: 'reports-schedules',
    path: 'reports/schedules/:reportBuilderId',
    element: <ReportSchedulesPage />,
    access: { anyOf: [permissions.viewNotification] },
  },
  {
    key: 'reports-options',
    path: 'reports/options',
    element: <ReportOptionsPage />,
    access: { allOf: [permissions.viewNotification, permissions.manageReportOptions] },
  },
  {
    key: 'reports-manage-options',
    path: 'reports/manage/options',
    element: <ManageNotificationOptionsPage />,
    access: { allOf: [permissions.viewNotification, permissions.manageReportOptions] },
  },
  {
    key: 'reports-view-notification-option',
    path: 'reports/view/:notificationOptionID',
    element: <NotificationOptionSchedulesPage />,
    access: { anyOf: [permissions.viewNotification] },
  },
  {
    key: 'fileupload',
    path: 'fileupload',
    element: <FileUpload />,
    access: { anyOf: [permissions.viewFileUpload] },
    nav: {
      sectionKey: 'practice',
      label: 'File Upload',
      Icon: UploadOutlined,
      order: 40,
    },
  },
]
